import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Apps as AppsIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import { useToast } from "../hooks/useToast";
import AppService from "../services/AppService";
import AIService from "../services/AIService";
import LegalService from "../services/LegalService";
import {
  App,
  CreateAppDTO,
  GooglePlayConnectionCheckResponse,
  UpdateAppDTO,
} from "../types/app";
import AppRegistrationFormSections, {
  AutoLegalGenerationConfig,
} from "../components/AppRegistrationFormSections";
import { CreateLegalDocDTO, LegalDocument } from "../types/legal";
import { APP_CATEGORIES } from "../utils/appCategories";

const APP_REGISTRATION_PREFILL_KEY = "app_registration_prefill";

const AI_COPY_MODELS = [
  { value: "", label: "Default model" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gpt-4.1", label: "OpenAI GPT-4.1" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
];
const LEGAL_TEXT_MODELS = [
  "gemini-2.5-flash-lite",
  "gemini-2.5-pro",
  "gpt-4.1-mini",
  "gpt-4.1",
  "o4-mini",
];
const COMMON_PERSONAL_INFO_ITEMS = [
  "Name",
  "Email address",
  "Phone number",
  "Device identifiers",
  "IP address",
  "Location data",
  "Payment information",
  "Usage analytics",
];
const DEFAULT_PERSONAL_INFO_ITEMS = [
  "Name",
  "Email address",
  "Device identifiers",
  "Usage analytics",
];
const DEFAULT_CONTACT_EMAIL = "violesoftware@gmail.com";
const PUBLIC_WEB_APP_ORIGIN = "https://translatorx-5774e.web.app";
const LEGAL_DOC_TYPES: Array<LegalDocument["type"]> = [
  "privacy",
  "tos",
  "eula",
  "cookie",
  "dpa",
];

const DEFAULT_FORM_SECTIONS = {
  core: true,
  store: false,
  legal: false,
  advanced: false,
};

const safeTrim = (value?: string): string => String(value ?? "").trim();
const getDefaultAutoLegalConfig = (): AutoLegalGenerationConfig => ({
  enabled: false,
  isSetupSaved: false,
  selectedTypes: ["privacy", "tos"],
  generationModel: AIService.getDefaultModel(),
  companyAddress: "",
  contactEmail: DEFAULT_CONTACT_EMAIL,
  website: "",
  jurisdiction: "US",
  appType: "mobile",
  dataCollection: {
    personalInfo: DEFAULT_PERSONAL_INFO_ITEMS,
    thirdPartyServices: [],
    cookies: false,
    analytics: false,
    advertising: false,
  },
  features: {
    userAccounts: true,
    inAppPurchases: true,
    userGeneratedContent: true,
    location: true,
    pushNotifications: true,
  },
});

const getPlatformStoreUrls = (input: {
  storeUrl?: string;
  androidStoreUrl?: string;
  iosStoreUrl?: string;
}) => {
  const legacyStoreUrl = safeTrim(input.storeUrl);
  const androidStoreUrl =
    safeTrim(input.androidStoreUrl) ||
    (legacyStoreUrl && AppService.validateStoreUrl(legacyStoreUrl, "android")
      ? legacyStoreUrl
      : "");
  const iosStoreUrl =
    safeTrim(input.iosStoreUrl) ||
    (legacyStoreUrl && AppService.validateStoreUrl(legacyStoreUrl, "ios")
      ? legacyStoreUrl
      : "");

  return { androidStoreUrl, iosStoreUrl };
};

const getPrimaryStoreUrl = (
  platform: "android" | "ios" | "both",
  androidStoreUrl: string,
  iosStoreUrl: string,
): string => {
  if (platform === "android") return androidStoreUrl;
  if (platform === "ios") return iosStoreUrl;
  return androidStoreUrl || iosStoreUrl;
};

const GOOGLE_PLAY_PACKAGE_REGEX = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
const FIREBASE_PROJECT_ID_REGEX = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

const AppRegistration: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useToastStateSync({ error });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<App | null>(null);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [expandedSections, setExpandedSections] = useState(
    DEFAULT_FORM_SECTIONS,
  );
  const [generatingNameVariation, setGeneratingNameVariation] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [aiCopyModel, setAiCopyModel] = useState("");
  const [verifyingGooglePlayConnection, setVerifyingGooglePlayConnection] =
    useState(false);
  const [googlePlayConnectionResult, setGooglePlayConnectionResult] =
    useState<GooglePlayConnectionCheckResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [favoriteLoadingAppId, setFavoriteLoadingAppId] = useState<string | null>(
    null,
  );
  const [autoLegalConfig, setAutoLegalConfig] = useState<AutoLegalGenerationConfig>(
    getDefaultAutoLegalConfig(),
  );

  // Form state
  const [formData, setFormData] = useState<CreateAppDTO>({
    name: "",
    description: "",
    category: "productivity",
    platform: "both",
    storeUrl: "",
    androidStoreUrl: "",
    iosStoreUrl: "",
    googlePlayPackageName: "",
    googlePlayDefaultLocale: "en-US",
    firebaseProjectId: "",
    ga4PropertyId: "",
    ga4StreamId: "",
    storeId: "",
    privacyPolicyUrl: "",
    termsOfServiceUrl: "",
    researchContext: undefined,
  });
  const [advancedData, setAdvancedData] = useState({
    whatItDoes: "",
    targetDemographics: "",
    monetization: "",
    keyFeatures: "",
    growthSignals: "",
    opportunities: "",
    risks: "",
    strategicRecommendations: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const getLegalPath = (type: LegalDocument["type"]) => {
    switch (type) {
      case "privacy":
        return "/privacy";
      case "tos":
        return "/terms";
      case "eula":
        return "/eula";
      case "cookie":
        return "/cookie";
      case "dpa":
        return "/dpa";
      default:
        return "/terms";
    }
  };

  const getPublicLegalLink = (type: LegalDocument["type"], appId: string) =>
    `${PUBLIC_WEB_APP_ORIGIN}${getLegalPath(type)}?app=${encodeURIComponent(appId)}`;

  useEffect(() => {
    if (user) {
      loadApps();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const raw = localStorage.getItem(APP_REGISTRATION_PREFILL_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<CreateAppDTO>;
      const { androidStoreUrl, iosStoreUrl } = getPlatformStoreUrls({
        storeUrl: parsed.storeUrl,
        androidStoreUrl: parsed.androidStoreUrl,
        iosStoreUrl: parsed.iosStoreUrl,
      });
      setEditingApp(null);
      setFormData({
        name: parsed.name || "",
        description: parsed.description || "",
        category: parsed.category || "productivity",
        platform: parsed.platform || "both",
        storeUrl: getPrimaryStoreUrl(
          parsed.platform || "both",
          androidStoreUrl,
          iosStoreUrl,
        ),
        androidStoreUrl,
        iosStoreUrl,
        googlePlayPackageName: parsed.googlePlayPackageName || "",
        googlePlayDefaultLocale: parsed.googlePlayDefaultLocale || "en-US",
        firebaseProjectId: parsed.firebaseProjectId || "",
        ga4PropertyId: parsed.ga4PropertyId || "",
        ga4StreamId: parsed.ga4StreamId || "",
        storeId: parsed.storeId || "",
        privacyPolicyUrl: parsed.privacyPolicyUrl || "",
        termsOfServiceUrl: parsed.termsOfServiceUrl || "",
        researchContext: parsed.researchContext,
      });
      const context = parsed.researchContext;
      setAdvancedData({
        whatItDoes: context?.whatItDoes || "",
        targetDemographics: (context?.targetDemographics || []).join("\n"),
        monetization: (context?.monetization || []).join("\n"),
        keyFeatures: (context?.keyFeatures || []).join("\n"),
        growthSignals: (context?.growthSignals || []).join("\n"),
        opportunities: (context?.opportunities || []).join("\n"),
        risks: (context?.risks || []).join("\n"),
        strategicRecommendations: (
          context?.strategicRecommendations || []
        ).join("\n"),
      });
      setShowAdvancedFields(Boolean(context));
      setAutoLegalConfig(getDefaultAutoLegalConfig());
      setGooglePlayConnectionResult(null);
      setOpenDialog(true);
    } catch (prefillError) {
      console.error(
        "Failed to parse app registration prefill payload",
        prefillError,
      );
    } finally {
      localStorage.removeItem(APP_REGISTRATION_PREFILL_KEY);
    }
  }, [user]);

  useEffect(() => {
    setGooglePlayConnectionResult(null);
  }, [formData.googlePlayPackageName]);

  const loadApps = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError("");
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (err) {
      setError("Failed to load apps. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (app?: App) => {
    setExpandedSections(DEFAULT_FORM_SECTIONS);
    if (app) {
      const { androidStoreUrl, iosStoreUrl } = getPlatformStoreUrls(app);
      setEditingApp(app);
      setFormData({
        name: app.name,
        description: app.description,
        category: app.category || "productivity",
        platform: app.platform,
        storeUrl: getPrimaryStoreUrl(app.platform, androidStoreUrl, iosStoreUrl),
        androidStoreUrl,
        iosStoreUrl,
        googlePlayPackageName: app.googlePlayPackageName || "",
        googlePlayDefaultLocale: app.googlePlayDefaultLocale || "en-US",
        firebaseProjectId: app.firebaseProjectId || "",
        ga4PropertyId: app.ga4PropertyId || "",
        ga4StreamId: app.ga4StreamId || "",
        storeId: app.storeId,
        privacyPolicyUrl: app.privacyPolicyUrl || "",
        termsOfServiceUrl: app.termsOfServiceUrl || "",
        researchContext: app.researchContext,
      });
      setAdvancedData({
        whatItDoes: app.researchContext?.whatItDoes || "",
        targetDemographics: (
          app.researchContext?.targetDemographics || []
        ).join("\n"),
        monetization: (app.researchContext?.monetization || []).join("\n"),
        keyFeatures: (app.researchContext?.keyFeatures || []).join("\n"),
        growthSignals: (app.researchContext?.growthSignals || []).join("\n"),
        opportunities: (app.researchContext?.opportunities || []).join("\n"),
        risks: (app.researchContext?.risks || []).join("\n"),
        strategicRecommendations: (
          app.researchContext?.strategicRecommendations || []
        ).join("\n"),
      });
      setShowAdvancedFields(Boolean(app.researchContext));
      setAutoLegalConfig(getDefaultAutoLegalConfig());
    } else {
      setEditingApp(null);
      setFormData({
        name: "",
        description: "",
        category: "productivity",
        platform: "both",
        storeUrl: "",
        androidStoreUrl: "",
        iosStoreUrl: "",
        googlePlayPackageName: "",
        googlePlayDefaultLocale: "en-US",
        firebaseProjectId: "",
        ga4PropertyId: "",
        ga4StreamId: "",
        storeId: "",
        privacyPolicyUrl: "",
        termsOfServiceUrl: "",
        researchContext: undefined,
      });
      setAdvancedData({
        whatItDoes: "",
        targetDemographics: "",
        monetization: "",
        keyFeatures: "",
        growthSignals: "",
        opportunities: "",
        risks: "",
        strategicRecommendations: "",
      });
      setShowAdvancedFields(false);
      setAutoLegalConfig(getDefaultAutoLegalConfig());
    }
    setFormErrors({});
    setGooglePlayConnectionResult(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingApp(null);
    setExpandedSections(DEFAULT_FORM_SECTIONS);
    setFormData({
      name: "",
      description: "",
      category: "productivity",
      platform: "both",
      storeUrl: "",
      androidStoreUrl: "",
      iosStoreUrl: "",
      googlePlayPackageName: "",
      googlePlayDefaultLocale: "en-US",
      firebaseProjectId: "",
      ga4PropertyId: "",
      ga4StreamId: "",
      storeId: "",
      privacyPolicyUrl: "",
      termsOfServiceUrl: "",
      researchContext: undefined,
    });
    setAdvancedData({
      whatItDoes: "",
      targetDemographics: "",
      monetization: "",
      keyFeatures: "",
      growthSignals: "",
      opportunities: "",
      risks: "",
      strategicRecommendations: "",
    });
    setShowAdvancedFields(false);
    setFormErrors({});
    setGooglePlayConnectionResult(null);
    setAutoLegalConfig(getDefaultAutoLegalConfig());
  };

  const handleSectionToggle =
    (section: keyof typeof DEFAULT_FORM_SECTIONS) =>
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: expanded,
      }));
    };

  const handleSaveAutoLegalSetup = () => {
    setAutoLegalConfig((prev) => ({ ...prev, isSetupSaved: true }));
    setExpandedSections((prev) => ({ ...prev, legal: false }));
  };

  const parseList = (value: string): string[] =>
    value
      .split(/\n|,/g)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

  const isValidHttpUrl = (value: string): boolean => {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const buildResearchContext = () => {
    const context = {
      whatItDoes: advancedData.whatItDoes.trim() || undefined,
      targetDemographics: parseList(advancedData.targetDemographics),
      monetization: parseList(advancedData.monetization),
      keyFeatures: parseList(advancedData.keyFeatures),
      growthSignals: parseList(advancedData.growthSignals),
      opportunities: parseList(advancedData.opportunities),
      risks: parseList(advancedData.risks),
      strategicRecommendations: parseList(
        advancedData.strategicRecommendations,
      ),
    };

    const hasAnyValue = Object.values(context).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });

    if (!hasAnyValue) {
      return undefined;
    }

    return context;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const androidStoreUrl = safeTrim(formData.androidStoreUrl);
    const iosStoreUrl = safeTrim(formData.iosStoreUrl);

    if (!safeTrim(formData.name)) {
      errors.name = "App name is required";
    }

    if (!safeTrim(formData.category)) {
      errors.category = "Category is required";
    }

    if (
      androidStoreUrl &&
      !AppService.validateStoreUrl(androidStoreUrl, "android")
    ) {
      errors.androidStoreUrl = "Invalid Google Play URL format";
    }

    if (iosStoreUrl && !AppService.validateStoreUrl(iosStoreUrl, "ios")) {
      errors.iosStoreUrl = "Invalid App Store URL format";
    }

    const googlePlayPackageName = safeTrim(formData.googlePlayPackageName);
    if (
      googlePlayPackageName &&
      !GOOGLE_PLAY_PACKAGE_REGEX.test(googlePlayPackageName)
    ) {
      errors.googlePlayPackageName = "Invalid Android package name format";
    }

    const firebaseProjectId = safeTrim(formData.firebaseProjectId);
    if (
      firebaseProjectId &&
      !FIREBASE_PROJECT_ID_REGEX.test(firebaseProjectId)
    ) {
      errors.firebaseProjectId =
        "Use a valid Firebase project ID (lowercase letters, digits, hyphens).";
    }

    const ga4PropertyId = safeTrim(formData.ga4PropertyId);
    if (
      ga4PropertyId &&
      !/^(properties\/)?\d+$/.test(ga4PropertyId)
    ) {
      errors.ga4PropertyId =
        "Use the numeric GA4 property ID, for example 123456789.";
    }

    if (
      safeTrim(formData.privacyPolicyUrl) &&
      !isValidHttpUrl(safeTrim(formData.privacyPolicyUrl))
    ) {
      errors.privacyPolicyUrl =
        "Privacy Policy URL must be a valid http(s) link";
    }
    if (
      safeTrim(formData.termsOfServiceUrl) &&
      !isValidHttpUrl(safeTrim(formData.termsOfServiceUrl))
    ) {
      errors.termsOfServiceUrl =
        "Terms of Service URL must be a valid http(s) link";
    }

    if (autoLegalConfig.enabled) {
      if (autoLegalConfig.selectedTypes.length === 0) {
        errors.autoLegalConfig = "Select at least one legal document type";
      }
      if (!safeTrim(autoLegalConfig.contactEmail)) {
        errors.autoLegalConfig = "Contact email is required for auto-legal generation";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateLegalDocumentsForApp = async (targetApp: {
    appId: string;
    name: string;
    description: string;
    storeId?: string;
  }) => {
    if (!user || !autoLegalConfig.enabled || autoLegalConfig.selectedTypes.length === 0) {
      return;
    }

    const generationModel =
      safeTrim(autoLegalConfig.generationModel) || AIService.getDefaultModel();
    const baseRequest: Omit<CreateLegalDocDTO, "appId" | "type"> = {
      generationModel,
      companyName: targetApp.name,
      companyAddress: safeTrim(autoLegalConfig.companyAddress),
      contactEmail:
        safeTrim(autoLegalConfig.contactEmail) || DEFAULT_CONTACT_EMAIL,
      website: safeTrim(autoLegalConfig.website) || undefined,
      jurisdiction: safeTrim(autoLegalConfig.jurisdiction) || "US",
      appType: autoLegalConfig.appType,
      dataCollection: {
        personalInfo: autoLegalConfig.dataCollection.personalInfo,
        thirdPartyServices: autoLegalConfig.dataCollection.thirdPartyServices,
        cookies: autoLegalConfig.dataCollection.cookies,
        analytics: autoLegalConfig.dataCollection.analytics,
        advertising: autoLegalConfig.dataCollection.advertising,
      },
      features: {
        userAccounts: autoLegalConfig.features.userAccounts,
        inAppPurchases: autoLegalConfig.features.inAppPurchases,
        userGeneratedContent: autoLegalConfig.features.userGeneratedContent,
        location: autoLegalConfig.features.location,
        pushNotifications: autoLegalConfig.features.pushNotifications,
      },
    };

    const publicIdentifiers = [targetApp.appId, safeTrim(targetApp.storeId)].filter(Boolean);

    for (const type of autoLegalConfig.selectedTypes) {
      await LegalService.generateDocument(
        user.email,
        {
          appId: targetApp.appId,
          type,
          ...baseRequest,
        },
        targetApp.name,
        targetApp.description,
        publicIdentifiers,
      );
    }

    const legalLinkUpdates: UpdateAppDTO = {};
    if (autoLegalConfig.selectedTypes.includes("privacy")) {
      legalLinkUpdates.privacyPolicyUrl = getPublicLegalLink(
        "privacy",
        targetApp.appId,
      );
    }
    if (autoLegalConfig.selectedTypes.includes("tos")) {
      legalLinkUpdates.termsOfServiceUrl = getPublicLegalLink(
        "tos",
        targetApp.appId,
      );
    }

    if (Object.keys(legalLinkUpdates).length > 0) {
      await AppService.updateApp(targetApp.appId, user.email, legalLinkUpdates);
    }
  };

  const handleSubmit = async () => {
    if (!user || !validateForm()) return;

    try {
      setLoading(true);
      setError("");
      const androidStoreUrl = safeTrim(formData.androidStoreUrl);
      const iosStoreUrl = safeTrim(formData.iosStoreUrl);
      const payload: CreateAppDTO | UpdateAppDTO = {
        ...formData,
        storeId: safeTrim(formData.storeId),
        googlePlayPackageName:
          safeTrim(formData.googlePlayPackageName) || undefined,
        googlePlayDefaultLocale: "en-US",
        firebaseProjectId: safeTrim(formData.firebaseProjectId) || undefined,
        ga4PropertyId:
          safeTrim(formData.ga4PropertyId).replace(/^properties\//i, "") ||
          undefined,
        ga4StreamId: safeTrim(formData.ga4StreamId) || undefined,
        privacyPolicyUrl: safeTrim(formData.privacyPolicyUrl) || undefined,
        termsOfServiceUrl: safeTrim(formData.termsOfServiceUrl) || undefined,
        storeUrl:
          getPrimaryStoreUrl(formData.platform, androidStoreUrl, iosStoreUrl) ||
          undefined,
        androidStoreUrl: androidStoreUrl || undefined,
        iosStoreUrl: iosStoreUrl || undefined,
        researchContext: showAdvancedFields
          ? buildResearchContext()
          : undefined,
      };

      if (editingApp) {
        await AppService.updateApp(editingApp.appId, user.email, payload);
        if (autoLegalConfig.enabled) {
          try {
            await generateLegalDocumentsForApp({
              appId: editingApp.appId,
              name: safeTrim(formData.name) || editingApp.name,
              description:
                safeTrim(formData.description) || editingApp.description || "",
              storeId: safeTrim(formData.storeId) || editingApp.storeId,
            });
          } catch (legalError) {
            console.error(legalError);
            setError(
              "App was updated, but legal docs auto-generation failed. You can generate them from Legal Documents.",
            );
          }
        }
        toast.success("App updated successfully.");
      } else {
        const createdApp = await AppService.createApp(
          user.email,
          payload as CreateAppDTO,
        );
        if (autoLegalConfig.enabled) {
          try {
            await generateLegalDocumentsForApp({
              appId: createdApp.appId,
              name: createdApp.name,
              description: createdApp.description,
              storeId: createdApp.storeId,
            });
          } catch (legalError) {
            console.error(legalError);
            setError(
              "App was created, but legal docs auto-generation failed. You can generate them from Legal Documents.",
            );
          }
        }
        toast.success("App created successfully.");
      }

      await loadApps();
      handleCloseDialog();
    } catch (err) {
      setError(`Failed to ${editingApp ? "update" : "create"} app`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyGooglePlayConnection = async () => {
    const packageName = safeTrim(formData.googlePlayPackageName);

    if (!packageName) {
      setFormErrors((prev) => ({
        ...prev,
        googlePlayPackageName: "Android package name is required for verification",
      }));
      return;
    }

    if (!GOOGLE_PLAY_PACKAGE_REGEX.test(packageName)) {
      setFormErrors((prev) => ({
        ...prev,
        googlePlayPackageName: "Invalid Android package name format",
      }));
      return;
    }

    try {
      setVerifyingGooglePlayConnection(true);
      setError("");
      setGooglePlayConnectionResult(null);

      const result = await AppService.verifyGooglePlayConnection(packageName);
      setGooglePlayConnectionResult(result);
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.googlePlayPackageName;
        return next;
      });
    } catch (verifyError: any) {
      console.error(verifyError);
      const responseData = verifyError?.responseData as
        | GooglePlayConnectionCheckResponse
        | undefined;
      if (responseData?.packageName) {
        setGooglePlayConnectionResult({
          success: false,
          packageName: responseData.packageName,
          editable: false,
          message:
            responseData.message ||
            verifyError?.message ||
            "Failed to verify Google Play connection.",
        });
      }
      setError(
        responseData?.message ||
          verifyError?.message ||
          "Failed to verify Google Play connection.",
      );
    } finally {
      setVerifyingGooglePlayConnection(false);
    }
  };

  const parseNameFromText = (value: string): string => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]) as { name?: string };
        if (parsed?.name) return String(parsed.name).trim();
      } catch {
        // ignore
      }
    }
    return trimmed
      .replace(/^["'\s]+|["'\s]+$/g, "")
      .split("\n")[0]
      .trim();
  };

  const parseDescriptionFromText = (value: string): string => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]) as { description?: string };
        if (parsed?.description) return String(parsed.description).trim();
      } catch {
        // ignore
      }
    }
    return trimmed.replace(/^["'\s]+|["'\s]+$/g, "").trim();
  };

  const handleGenerateNameVariation = async () => {
    const whatItDoes = advancedData.whatItDoes.trim();
    if (!whatItDoes) {
      setError('Add "What It Does" first to generate an AI app name.');
      return;
    }

    try {
      setGeneratingNameVariation(true);
      setError("");
      const prompt = [
        "You are a mobile app branding expert.",
        "Generate one app name based only on the app functionality provided below.",
        "",
        `What it does: ${whatItDoes}`,
        "",
        "Rules:",
        "- Return only one name.",
        "- Keep it short (2-4 words).",
        '- Base the name strictly on the functionality in "What it does".',
        "- Do not use category/platform/other metadata.",
        "- Do not include punctuation-heavy or keyword-stuffed names.",
      ].join("\n");

      const structured = await AIService.generateJSON<{ name?: string }>(
        prompt,
        {
          model: aiCopyModel || undefined,
          structuredOutputName: "app_name_variation",
          structuredOutputSchema: {
            type: "object",
            properties: { name: { type: "string" } },
            required: ["name"],
          },
          temperature: 0.7,
          maxTokens: 120,
        },
      );

      let nextName = String(structured?.name || "").trim();
      if (!nextName) {
        const fallback = await AIService.generateText(prompt, {
          model: aiCopyModel || undefined,
          temperature: 0.7,
          maxTokens: 120,
        });
        nextName = parseNameFromText(fallback.text);
      }

      if (!nextName) {
        throw new Error("AI did not return a valid app name variation.");
      }

      setFormData((prev) => ({ ...prev, name: nextName }));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to generate app name variation.");
    } finally {
      setGeneratingNameVariation(false);
    }
  };

  const handleGenerateDescription = async () => {
    const whatItDoes = advancedData.whatItDoes.trim();
    if (!whatItDoes) {
      setError('Add "What It Does" first to generate an AI app description.');
      return;
    }

    try {
      setGeneratingDescription(true);
      setError("");
      const prompt = [
        "You are an app store copywriter.",
        "Generate one concise app description based only on the functionality provided below.",
        "",
        `What it does: ${whatItDoes}`,
        "",
        "Rules:",
        "- Return only the description text.",
        "- 2 to 4 sentences.",
        "- Make it clear and benefit-driven.",
        '- Base content strictly on "What it does".',
        "- Do not invent unsupported claims.",
      ].join("\n");

      const structured = await AIService.generateJSON<{ description?: string }>(
        prompt,
        {
          model: aiCopyModel || undefined,
          structuredOutputName: "app_description_generation",
          structuredOutputSchema: {
            type: "object",
            properties: { description: { type: "string" } },
            required: ["description"],
          },
          temperature: 0.6,
          maxTokens: 220,
        },
      );

      let nextDescription = String(structured?.description || "").trim();
      if (!nextDescription) {
        const fallback = await AIService.generateText(prompt, {
          model: aiCopyModel || undefined,
          temperature: 0.6,
          maxTokens: 220,
        });
        nextDescription = parseDescriptionFromText(fallback.text);
      }

      if (!nextDescription) {
        throw new Error("AI did not return a valid app description.");
      }

      setFormData((prev) => ({ ...prev, description: nextDescription }));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to generate app description.");
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleDeleteClick = (app: App) => {
    setAppToDelete(app);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!user || !appToDelete) return;

    try {
      setLoading(true);
      setError("");
      await AppService.deleteApp(appToDelete.appId, user.email);
      await loadApps();
      setDeleteConfirmOpen(false);
      setAppToDelete(null);
    } catch (err) {
      setError("Failed to delete app");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (app: App) => {
    if (!user) return;

    const nextFavorite = !Boolean(app.favorite);
    const previousApps = apps;
    setApps((prev) =>
      prev.map((candidate) =>
        candidate.appId === app.appId
          ? { ...candidate, favorite: nextFavorite }
          : candidate,
      ),
    );
    setFavoriteLoadingAppId(app.appId);
    try {
      setError("");
      await AppService.updateApp(app.appId, user.email, { favorite: nextFavorite });
    } catch (err) {
      setApps(previousApps);
      setError("Failed to update favorite status.");
      console.error(err);
    } finally {
      setFavoriteLoadingAppId(null);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "android":
        return <AndroidIcon sx={{ fontSize: "1.2rem", color: "#3DDC84" }} />;
      case "ios":
        return <AppleIcon sx={{ fontSize: "1.2rem", color: "#000" }} />;
      case "both":
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <AndroidIcon sx={{ fontSize: "1.2rem", color: "#3DDC84" }} />
            <AppleIcon sx={{ fontSize: "1.2rem", color: "#000" }} />
          </Box>
        );
      default:
        return null;
    }
  };

  const filteredApps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const searchedApps = !query
      ? apps
      : apps.filter((app) =>
      [
        app.name,
        app.appId,
        app.storeId,
        app.googlePlayPackageName,
        app.firebaseProjectId,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );

    return [...searchedApps].sort((a, b) => {
      const favoriteDelta = Number(Boolean(b.favorite)) - Number(Boolean(a.favorite));
      if (favoriteDelta !== 0) return favoriteDelta;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
  }, [apps, searchQuery]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            <AppsIcon
              sx={{ mr: 2, verticalAlign: "middle", fontSize: "2rem" }}
            />
            App Registration
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Register and manage your apps in the Success Platform
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Add New App
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Apps List */}
      {loading && apps.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : apps.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", backgroundColor: "action.hover" }}>
          <AppsIcon sx={{ fontSize: "4rem", color: "#bdbdbd", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
            No apps registered yet
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Get started by adding your first app
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Your First App
          </Button>
        </Paper>
      ) : (
        <Box>
          <TextField
            fullWidth
            placeholder="Search apps by name, app ID, bundle ID, package, or Firebase project..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
            }}
          />
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>App Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>App ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Platform</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Bundle Identifier (iOS)
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Android Package</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Firebase Project</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Store URLs</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", py: 2, textAlign: "center" }}
                      >
                        No apps match "{searchQuery}".
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApps.map((app) => (
                    <TableRow key={app.appId} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{app.name}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                        >
                          {app.appId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getPlatformIcon(app.platform)}
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {app.platform}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                        >
                          {app.storeId || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {app.googlePlayPackageName ? (
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                          >
                            {app.googlePlayPackageName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {app.firebaseProjectId ? (
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                          >
                            {app.firebaseProjectId}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {app.androidStoreUrl || app.iosStoreUrl ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {app.androidStoreUrl && (
                              <a
                                href={app.androidStoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1976d2", textDecoration: "none" }}
                              >
                                Google Play
                              </a>
                            )}
                            {app.iosStoreUrl && (
                              <a
                                href={app.iosStoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1976d2", textDecoration: "none" }}
                              >
                                App Store
                              </a>
                            )}
                          </Box>
                        ) : app.storeUrl ? (
                          <a
                            href={app.storeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "none" }}
                          >
                            View Store
                          </a>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{app.createdAt.toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleFavorite(app)}
                          disabled={favoriteLoadingAppId === app.appId}
                          sx={{ mr: 1 }}
                        >
                          {app.favorite ? (
                            <StarIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(app)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(app)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 22%)",
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.4 }}>
              {editingApp ? "Edit App" : "Add New App"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Capture your app details once, then reuse them across listings,
              legal assets, research, and store-facing workflows.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <AppRegistrationFormSections
            aiCopyModel={aiCopyModel}
            aiCopyModels={AI_COPY_MODELS}
            setAiCopyModel={setAiCopyModel}
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            categories={APP_CATEGORIES}
            generatingNameVariation={generatingNameVariation}
            generatingDescription={generatingDescription}
            handleGenerateNameVariation={handleGenerateNameVariation}
            handleGenerateDescription={handleGenerateDescription}
            showAdvancedFields={showAdvancedFields}
            setShowAdvancedFields={setShowAdvancedFields}
            advancedData={advancedData}
            setAdvancedData={setAdvancedData}
            expandedSections={expandedSections}
            verifyingGooglePlayConnection={verifyingGooglePlayConnection}
            googlePlayConnectionResult={googlePlayConnectionResult}
            onVerifyGooglePlayConnection={handleVerifyGooglePlayConnection}
            onSectionToggle={handleSectionToggle}
            autoLegalConfig={autoLegalConfig}
            setAutoLegalConfig={setAutoLegalConfig}
            legalModelOptions={LEGAL_TEXT_MODELS}
            legalDocTypeOptions={LEGAL_DOC_TYPES}
            commonPersonalInfoOptions={COMMON_PERSONAL_INFO_ITEMS}
            onSaveAutoLegalSetup={handleSaveAutoLegalSetup}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            justifyContent: "space-between",
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "rgba(248,250,252,0.75)",
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Apps created here can be selected in various Success Platform workflows to prefill app details and research context, saving you time and ensuring consistency across your app's presence in the platform.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{ minWidth: 132, borderRadius: 999 }}
            >
            {loading ? (
              <CircularProgress size={24} />
            ) : editingApp ? (
              "Update"
            ) : (
              "Create"
            )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete App</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{appToDelete?.name}</strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppRegistration;
